import { GeoJSONFeature, Layer, Map } from 'mapbox-gl';

import { HIGHLIGHT_LAYERS } from '../mapData/highlightLayers';
import { LayerWithToggleableGroup } from '../types/types';

const clearActiveHighlight = (map: Map, highlightLayer: keyof typeof HIGHLIGHT_LAYERS): void => {
  const { filterProperty, sourceLayers, highlightLayers, getIconSizes } =
    HIGHLIGHT_LAYERS[highlightLayer];

  // reset icon sizes in source map layers
  sourceLayers.forEach(({ id }) => {
    if (hasLayer(map, id)) {
      const { normalIconSize } = getIconSizes(id);
      map.setLayoutProperty(id, 'icon-size', normalIconSize);
    }
  });

  // remove highlights in highlight layers
  highlightLayers.forEach(({ id }) => {
    if (hasLayer(map, id)) {
      map.setFilter(id, ['==', filterProperty, '']);
    }
  });
};

export const clearActiveHighlights = (map: Map): void => {
  Object.keys(HIGHLIGHT_LAYERS).forEach(highlightLayer => {
    clearActiveHighlight(map, highlightLayer as keyof typeof HIGHLIGHT_LAYERS);
  });
};

export const checkAndReturnCatchIconData = (
  features: GeoJSONFeature[],
  longitude: any,
  latitude: any,
): string => {
  const {
    // @ts-ignore
    properties: { external_id: dataPointId },
  } = features[0];

  if (!dataPointId) {
    throw new Error(
      `No externalId found when clicking the icon: Id: ${dataPointId}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }

  if (typeof dataPointId !== 'string') {
    throw new Error(`The provided externalId was not a string. Id: ${dataPointId}`);
  }

  return dataPointId;
};

export const checkAndReturnPointsOfInterestIconData = (
  features: GeoJSONFeature[],
  longitude: any,
  latitude: any,
): number => {
  const {
    // @ts-ignore
    properties: { id },
  } = features[0];
  if (!id) {
    throw new Error(
      `No Id found when clicking the point of interest: Id: ${id}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }
  if (typeof id !== 'number') {
    throw new Error(`The provided externalId was not a number. Id: ${id}`);
  }

  return id;
};

export const checkAndReturnWaypointIconData = (
  features: GeoJSONFeature[],
  longitude: any,
  latitude: any,
): string => {
  const {
    // @ts-ignore
    properties: { external_id: dataPointId, id },
  } = features[0];
  if (!dataPointId) {
    throw new Error(
      `No Id found when clicking the point of interest: Id: ${id}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }
  if (typeof dataPointId !== 'string') {
    throw new Error(`The provided externalId was not a string. Id: ${id}`);
  }

  return dataPointId;
};

export const highlightMapIcon = ({
  map,
  highlightLayer,
  value,
  layer,
}: {
  map: Map;
  highlightLayer: keyof typeof HIGHLIGHT_LAYERS;
  value: string | number;
  layer: Layer & LayerWithToggleableGroup;
}): void => {
  const { filterProperty, highlightLayers, getIconSizes } = HIGHLIGHT_LAYERS[highlightLayer];
  const { normalIconSize, enlargedIconSize } = getIconSizes(layer.id);

  // enlarge icon in source map layer
  if (hasLayer(map, layer.id)) {
    map.setLayoutProperty(layer.id, 'icon-size', [
      'match',
      ['get', filterProperty],
      value,
      enlargedIconSize,
      normalIconSize,
    ]);
  }

  // add highlight in highlight layer
  highlightLayers.forEach(({ id }) => {
    if (hasLayer(map, id)) {
      map.setFilter(id, ['==', filterProperty, value]);
    }
  });
};

/**
 * Safely checks if a layer exists on the map.
 * Mapbox's getLayer() is typed to always return a Layer, but actually returns undefined
 * when the layer doesn't exist. This helper provides proper runtime type safety.
 */
export const hasLayer = (map: Map, layerId: string): boolean => {
  return Boolean(map.getLayer(layerId) as unknown);
};

/**
 * Safely checks if a source exists on the map.
 * Mapbox's getSource() is typed to always return a Source, but actually returns undefined
 * when the source doesn't exist. This helper provides proper runtime type safety.
 */
export const hasSource = (map: Map, sourceId: string): boolean => {
  return Boolean(map.getSource(sourceId) as unknown);
};
